import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { debounce } from '../../../base/config/functions.any';
import { ZINDEX_DIALOG_PORTAL } from '../../constants';
/**
 * Component meant to render a drawer at the bottom of the screen,
 * by creating a portal containing the component's children.
 *
 * @returns {ReactElement}
 */
function DialogPortal({ children, className, style, getRef, setSize, targetSelector, onVisible }) {
    const videoSpaceWidth = useSelector((state) => state['features/base/responsive-ui'].videoSpaceWidth);
    const [portalTarget] = useState(() => {
        const portalDiv = document.createElement('div');
        portalDiv.style.visibility = 'hidden';
        return portalDiv;
    });
    const timerRef = useRef();
    useEffect(() => {
        if (style) {
            // 切换 left/right 定位时清掉对侧属性，避免 DialogPortal 残留旧值导致贴边菜单跑出视口
            const objStyle = portalTarget.style;
            if (style.right != null && style.left == null) {
                objStyle.left = 'auto';
            }
            if (style.left != null && style.right == null) {
                objStyle.right = 'auto';
            }
            for (const styleProp of Object.keys(style)) {
                objStyle[styleProp] = style[styleProp];
            }
        }
        if (className) {
            portalTarget.className = className;
        }
    }, [style, className, portalTarget]);
    useEffect(() => {
        if (portalTarget && getRef) {
            getRef(portalTarget);
            portalTarget.style.zIndex = `${ZINDEX_DIALOG_PORTAL}`;
        }
    }, [portalTarget, getRef]);
    useEffect(() => {
        const size = {
            width: 1,
            height: 1
        };
        const debouncedResizeCallback = debounce((entries) => {
            const { contentRect } = entries[0];
            if (contentRect.width !== size.width || contentRect.height !== size.height) {
                setSize?.(contentRect);
                clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => {
                    portalTarget.style.visibility = 'visible';
                    onVisible?.();
                }, 100);
            }
        }, 20); // 20ms delay
        // Create and observe ResizeObserver
        const observer = new ResizeObserver(debouncedResizeCallback);
        const target = targetSelector ? portalTarget.querySelector(targetSelector) : portalTarget;
        if (document.body) {
            document.body.appendChild(portalTarget);
            observer.observe(target ?? portalTarget);
        }
        return () => {
            observer.unobserve(target ?? portalTarget);
            if (document.body) {
                document.body.removeChild(portalTarget);
            }
        };
    }, [videoSpaceWidth]);
    return ReactDOM.createPortal(children, portalTarget);
}
export default DialogPortal;
